import { useRef } from "react";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const LOBE_ICON = (id: string) => `https://unpkg.com/@lobehub/icons-static-svg@latest/icons/${id.replace('-color', '')}.svg`;
const LOBE_COLOR_ICON = (id: string) => `https://unpkg.com/@lobehub/icons-static-svg@latest/icons/${id.includes('-color') ? id : id + '-color'}.svg`;
const DEVICON = (id: string, variant: string = "original") => `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${id}/${id}-${variant}.svg`;


export const skillCategories = [
  {
    title: "DevOps & Cloud",
    description: "Orchestrating scalable infrastructure.",
    color: "from-blue-500/10 to-transparent",
    border: "group-hover:border-cyan-500/30",
    skills: [
      { name: "AWS", logo: LOBE_COLOR_ICON('aws') },
      { name: "Docker", logo: DEVICON('docker') },
      { name: "Kubernetes", logo: DEVICON('kubernetes') },
      { name: "Terraform", logo: DEVICON('terraform') },
      { name: "Linux", logo: DEVICON('linux') },
    ],
  },
  {
    title: "Scripting & Languages",
    description: "Speaking the language of machines.",
    color: "from-yellow-500/10 to-transparent",
    border: "group-hover:border-yellow-500/30",
    skills: [
      { name: "Python", logo: DEVICON('python') },
      { name: "JavaScript", logo: DEVICON('javascript') },
      { name: "TypeScript", logo: DEVICON('typescript') },
      { name: "HTML", logo: DEVICON('html5') },
      { name: "CSS", logo: DEVICON('css3') },
    ],
  },
  {
    title: "Monitoring & CI/CD",
    description: "Keeping systems healthy and automated.",
    color: "from-green-500/10 to-transparent",
    border: "group-hover:border-green-500/30",
    skills: [
      { name: "Prometheus", logo: DEVICON('prometheus') },
      { name: "Grafana", logo: DEVICON('grafana') },
      { name: "Jenkins", logo: DEVICON('jenkins') },
      { 
        name: "GitHub", 
        logo: "github", 
        isSvg: true // Custom handler for github
      },
      { name: "Git", logo: DEVICON('git') },
    ],
  },
  {
    title: "AI & Automation",
    description: "Building intelligent workflows.",
    color: "from-purple-500/10 to-transparent",
    border: "group-hover:border-purple-500/30",
    skills: [
      { name: "OpenAI", logo: LOBE_ICON('openai') },
      { name: "Claude", logo: LOBE_COLOR_ICON('claude') },
      { name: "Claude Code", logo: LOBE_COLOR_ICON('claudecode') },
      { name: "Gemini", logo: LOBE_COLOR_ICON('gemini') },
      { name: "n8n", logo: LOBE_COLOR_ICON('n8n') },
    ],
  },
  {
    title: "Backend Development",
    description: "Building robust APIs and microservices.",
    color: "from-orange-500/10 to-transparent",
    border: "group-hover:border-orange-500/30",
    skills: [
      { name: "Python", logo: DEVICON('python') },
      { name: "FastAPI", logo: DEVICON('fastapi') },
      { name: "PostgreSQL", logo: DEVICON('postgresql') },
      { name: "Redis", logo: DEVICON('redis') },
    ],
  },
  {
    title: "AI & ML Tools",
    description: "Intelligent tools and platforms.",
    color: "from-indigo-500/10 to-transparent",
    border: "group-hover:border-indigo-500/30",
    skills: [
      { name: "Hugging Face", logo: LOBE_COLOR_ICON('huggingface') },
      { name: "Perplexity", logo: LOBE_COLOR_ICON('perplexity') },
      { name: "Cursor", logo: LOBE_ICON('cursor') },
      { name: "Ollama", logo: LOBE_ICON('ollama') },
      { name: "Antigravity", logo: LOBE_COLOR_ICON('antigravity') },
    ],
  },
];

// Black single-colour marks disappear on dark tiles, so flip them in dark mode
const darkInvert = (skill: { name: string; logo: string }) => {
  if (skill.name === "AWS") return "dark:invert dark:hue-rotate-180"; // black wordmark, keep the orange
  return skill.logo.includes("lobehub") && !skill.logo.includes("-color") ? "dark:invert" : "";
};

export type Skill = { name: string; logo: string; isSvg?: boolean };

/** One skill's mark: the logo image, or the inline GitHub icon. */
export function SkillLogo({ skill, size = "w-5 h-5" }: { skill: Skill; size?: string }) {
  return (
    <>
      {skill.isSvg && skill.name === "GitHub" ? (
                        <svg className={`${size} text-foreground`} viewBox="0 0 24 24" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                      ) : (
                        <img
                          src={skill.logo}
                          loading="lazy"
                          className={`${size} object-contain ${darkInvert(skill)}`}
                          alt={skill.name}
                          onError={(e) => {
                             const target = e.target as HTMLImageElement;
                             target.style.display = 'none';
                          }}
                        />
                      )}
    </>
  );
}

export function Skills() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  return (
    <section
      ref={sectionRef}
      id="skills"
      className="relative py-6 md:py-8 px-4 sm:px-6 bg-background overflow-hidden flex flex-col justify-center"
    >

      <div className="site-frame relative z-10">
        
        {/* Header */}
        <div className="mb-4 md:mb-5 text-left">
          <h2
            ref={titleRef}
            className="text-[15px] md:text-base font-semibold tracking-tight text-foreground"
          >
            Skills
          </h2>
        </div>

        {/* Icon grid — white boxes, name shown on hover */}
        <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-8 gap-2 sm:flex sm:flex-wrap">
            {(() => {
              const seen = new Set();
              const uniqueSkills = skillCategories.flatMap(cat => cat.skills).filter(skill => {
                if (seen.has(skill.name)) return false;
                seen.add(skill.name);
                return true;
              });

              return uniqueSkills.map((skill: any, idx) => (
                <Tooltip key={idx}>
                  <TooltipTrigger asChild>
                    <motion.div whileHover={{ y: -2 }} className="aspect-square w-full sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border border-border bg-card cursor-default">
                      <SkillLogo skill={skill} />
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{skill.name}</p>
                  </TooltipContent>
                </Tooltip>
              ));
            })()}
          </div>
        </TooltipProvider>
      </div>
    </section>
  );
}
